import React from 'react'
import PropTypes from 'prop-types'
import { Box, H3, H6 } from '@maui/elements'
import { ResponsiveBar } from '@nivo/bar'

const titleize = (words) => {
  return words?.split(' ')
    .map(word => word && (word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ')
}

const Divider = () => (
  <Box height='100px' width='1px' backgroundColor='colorActionSecondary'/>
)

function CvssString({ vectorString }) {
  return <Box
    fontSize='12px'
    fontWeight={400}
    lineHeight='12px'
    color='colorTextDisabled'
    display='flex'
    alignItems='center'
    justifyContent='end'
    marginTop='-30px'
    letterSpacing='1px'
  >
    {vectorString}
  </Box>
}

const MyResponsiveBar = ({ data, colors }) => (
  <ResponsiveBar
    data={data}
    colors={colors}
    keys={[
      'base',
      'temporal'
    ]}
    isInteractive={false}
    maxValue={10.0}
    theme={{ textColor: 'var(--maui-color-text-deemphasis)', fontSize: 'var(--maui-typography-chart-label-font-size)' }}
    enableGridY={true}
    enableLabel={false}
    indexBy="ratings"
    margin={{ top: 20, bottom: 50, left: 10, right: 10 }}
    padding={0.3}
    layout="horizontal"
    valueScale={{ type: 'linear' }}
    indexScale={{ type: 'band', round: true }}
    axisTop={null}
    axisRight={null}
    axisLeft={null}
    axisBottom={{
      tickSize: 0,
      tickPadding: 0,
      tickValues: [0.0, 2.5, 5.0, 7.5, 10.0],
    }}
    legendLabel={datum => titleize(datum.id)}
    legends={[
      {
        dataFrom: 'keys',
        anchor: 'top',
        direction: 'row',
        justify: false,
        translateX: 80,
        translateY: -15,
        itemsSpacing: 2,
        itemWidth: 65,
        itemHeight: 10,
        itemDirection: 'left-to-right',
        itemOpacity: 1,
        symbolSize: 12
      }
    ]}
    role="application"
  />
)

const CvssV2Block = ({ v2 }) => {
  if (!v2) return null
  const {
    temporal_score: v2Temp,
    base_score: v2Base,
    vector_string: v2Vs
  } = v2

  const data = [
    {
      ratings: 'temporal-v2',
      temporal: v2Temp,
    },
    {
      ratings: 'base-v2',
      base: v2Base,
    },
  ]

  const colors = ['var(--maui-color-data-visualization-qualitative-01)', 'var(--maui-color-data-visualization-qualitative-02)']

  return (
    <Box>
      <Box mb='16px'>
        <H6>Version 2.0</H6>
      </Box>
      <Box display='flex'>
        <Box width='25%' pt='12px' pb='12px'>
          {v2Base && (
            <Box pb='8px'>
              <Box textStyle='sectionLabel1' letterSpacing='2px'>Base</Box>
              <H3 margin='0'>{v2Base}</H3>
            </Box>
          )}
          {v2Temp && (
            <Box>
              <Box textStyle='sectionLabel1' letterSpacing='2px'>Temporal</Box>
              <H3 margin='0'>{v2Temp}</H3>
            </Box>
          )}
        </Box>
        <Divider />
        <Box width='75%'>
          <Box width='100%' height='135px' fontWeight='700'>
            <MyResponsiveBar data={data} colors={colors} />
            <CvssString vectorString={v2Vs} />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const CvssV3Block = ({ v3 }) => {
  if (!v3) return null
  const {
    temporal_score: v3Temp,
    base_score: v3Base,
    vector_string: v3Vs
  } = v3

  const data = [
    {
      ratings: 'temporal-v3',
      temporal: v3Temp
    },
    {
      ratings: 'base-v3',
      base: v3Base,
    },
  ]

  const colors = ['var(--maui-color-data-visualization-qualitative-05)', 'var(--maui-color-data-visualization-qualitative-04)']

  return (
    <Box>
      <Box mb='16px'>
        <H6>Version 3.0</H6>
      </Box>
      <Box display='flex'>
        <Box width='25%' pt='12px' pb='12px'>
          {v3Base && (
            <Box pb='8px'>
              <Box textStyle='sectionLabel1' letterSpacing='2px'>Base</Box>
              <H3 margin='0'>{v3Base}</H3>
            </Box>
          )}
          {v3Temp && (
            <Box>
              <Box textStyle='sectionLabel1' letterSpacing='2px'>Temporal</Box>
              <H3 margin='0'>{v3Temp}</H3>
            </Box>
          )}
        </Box>
        <Divider />
        <Box width='75%'>
          <Box width='100%' height='135px' fontWeight='700'>
            <MyResponsiveBar data={data} colors={colors} />
            <CvssString vectorString={v3Vs}></CvssString>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const CvssV3Point1Block = ({ v3 }) => {
  if (!v3) return null
  const {
    temporal_score: v3Temp,
    base_score: v3Base,
    vector_string: v3Vs
  } = v3

  const data = [
    {
      ratings: 'temporal-v3.1',
      temporal: v3Temp
    },
    {
      ratings: 'base-v3.1',
      base: v3Base,
    },
  ]

  const colors = ['var(--maui-color-data-visualization-qualitative-07)', 'var(--maui-color-data-visualization-qualitative-06)']

  return (
    <Box>
      <Box mb='16px'>
        <H6>Version 3.1</H6>
      </Box>
      <Box display='flex'>
        <Box width='25%' pt='12px' pb='12px'>
          {v3Base && (
            <Box pb='8px'>
              <Box textStyle='sectionLabel1' letterSpacing='2px'>Base</Box>
              <H3 margin='0'>{v3Base}</H3>
            </Box>
          )}
          {v3Temp && (
            <Box>
              <Box textStyle='sectionLabel1' letterSpacing='2px'>Temporal</Box>
              <H3 margin='0'>{v3Temp}</H3>
            </Box>
          )}
        </Box>
        <Divider/>
        <Box width='75%' height='135px' fontWeight='700'>
          <MyResponsiveBar data={data} colors={colors}/>
          <CvssString vectorString={v3Vs}></CvssString>
        </Box>
      </Box>
    </Box>
  )
}

export function CvssPanel({ vulnScore }) {
  if (!vulnScore) return null
  return (
    <>
      {vulnScore['v3.1'] && <CvssV3Point1Block v3={vulnScore['v3.1']}/>}
      {vulnScore['v3.0'] && <CvssV3Block v3={vulnScore['v3.0']} />}
      {vulnScore['v2.0'] && (
        <Box pt='12px'>
          <CvssV2Block v2={vulnScore['v2.0']}/>
        </Box>
      )}

    </>
  )
}

CvssPanel.propTypes = {
  vulnScore: PropTypes.object
}

export default CvssPanel
